import { useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { first: "", last: "", email: "", service: "", message: "" };

const SERVICES = [
  "Saltwater Aquariums",
  "Freshwater Aquariums",
  "Reef Aquariums",
  "Aquarium Maintenance",
  "Design & Installation",
  "Water Delivery",
  "6-Month Guarantee",
  "24/7 Emergency Support",
  "General Inquiry",
];

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your first name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: `${form.first} ${form.last}`.trim(),
        email: form.email,
        project: form.service,
        message: form.message,
      });
      toast.success("Thank you — we'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const line =
    "w-full bg-transparent border-b border-[#0d1026]/25 py-2.5 font-body text-[#0d1026] focus:outline-none focus:border-[#5a3fe0] transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-10 lg:py-14">
      <div className="w-full max-w-[1860px] mx-auto px-5 lg:px-14 grid lg:grid-cols-2 gap-7 items-stretch">
        {/* Left card: owner intro */}
        <div className="rounded-[2rem] bg-[#5a3fe0] p-8 sm:p-10 lg:p-14 flex flex-col sm:flex-row items-center gap-8">
          <div className="flex-1 text-center sm:text-left">
            <h2 className="font-title text-4xl lg:text-6xl leading-[0.95] text-white">
              Jimmie<br />Hernandez
            </h2>
            <p className="font-body font-light text-base lg:text-xl text-white/85 mt-4">
              Owner &amp; Aquarium Specialist
            </p>
          </div>
          <img
            src="/media/jimmie.jpg"
            alt="Jimmie Hernandez, owner of Seatech Aquariums"
            data-testid="owner-photo"
            className="w-2/3 sm:w-[42%] max-w-[320px] rounded-2xl object-cover shadow-2xl shadow-black/30"
          />
        </div>

        {/* Right card: contact form */}
        <div className="rounded-[2rem] bg-[#5a3fe0] p-8 lg:p-12">
          <h2 className="font-title text-3xl lg:text-5xl text-white text-center lg:text-right mb-8">
            Say hello. We don't bite.
          </h2>

          <div className="rounded-[1.5rem] bg-white p-8 lg:p-12">
            <h3 className="font-title text-2xl lg:text-3xl text-[#0d1026] mb-8">Contact us</h3>
            <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label className="font-body text-sm text-[#0d1026]/70">First name *</label>
                <input data-testid="contact-first" className={line} value={form.first} onChange={set("first")} />
              </div>
              <div>
                <label className="font-body text-sm text-[#0d1026]/70">Last name</label>
                <input data-testid="contact-last" className={line} value={form.last} onChange={set("last")} />
              </div>
              <div className="sm:col-span-2">
                <label className="font-body text-sm text-[#0d1026]/70">Email *</label>
                <input data-testid="contact-email-input" type="email" className={line} value={form.email} onChange={set("email")} />
              </div>
              <div className="sm:col-span-2">
                <label className="font-body text-sm text-[#0d1026]/70">Service you're inquiring about</label>
                <select
                  data-testid="contact-service"
                  className={`${line} cursor-pointer`}
                  value={form.service}
                  onChange={set("service")}
                >
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-body text-sm text-[#0d1026]/70">Write a message</label>
                <textarea data-testid="contact-message" rows={3} className={`${line} resize-none`} value={form.message} onChange={set("message")} />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[#5a3fe0] px-10 py-3.5 font-body text-white hover:bg-[#4a30c8] transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
